// The save of the General block: the partial body (N16), the checks before sending, the answers of
// the server (field errors, concurrent change, delivery confirmation) and the fresh marker that the
// next save uses. The block gives it what the user typed (`batch`) and how to read the fields.
import { useEffect, useRef, useState } from 'react'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { saveGeneralDetail } from '../detailApi'
import { isFieldValidationError, splitFieldErrors } from '../fieldErrorKeys'
import { legacyRowErrors, normalizeDecimal, parsePriority } from '../legacyRowRules'
import { isConcurrentChangeError, concurrentChangeConflict } from '../concurrentChangeConflict'
import { toInstantIso } from '../detailDates'

const useGeneralSave = ({ batch, header, sheet, fabricationOrderId, extendedColumns, valueOf, extendedValueOf, isModified, sheetSource, onSaved, registerSave }) => {
  const { t_ } = useSmartTranslation()
  const source = header || {}
  const { setSavedBatch } = batch
  const [fieldErrors, setFieldErrors] = useState({})
  const [conflict, setConflict] = useState(null)
  const [deliveryConfirmation, setDeliveryConfirmation] = useState(null)
  const [saving, setSaving] = useState(false)
  // A second click can arrive before the first one re-renders the buttons as disabled.
  const saveInFlight = useRef(false)
  const [saveError, setSaveError] = useState('')
  // `expectedLastModified` es OBLIGATORIO en N16 (ausente o ilegible ⇒ 400), y la ficha puede guardar
  // varias veces sin cerrarse: se usa la marca fresca del último guardado y, si no la hay, la que
  // trajo la cabecera. Cuando llega una cabecera nueva (relectura tras guardar), manda ella.
  // Lives in the container (it survives this block unmounting) and is tied to the header it was
  // read against: a newer header makes it stale by itself, derived during render.
  const headerMarker = String(source.lastModified ?? '')
  const freshMarker = batch.freshMarker.forLastModified === headerMarker ? batch.freshMarker.value : ''
  const expectedMarker = freshMarker || source.lastModified
  // S313 (enmienda del addendum 9, arbitrada por el hub): la marca **ausente** sigue siendo 400 —
  // protege del cliente que se olvidó— pero la marca **presente con `null`** es legal y significa
  // "leí una orden sin marca". Es la misma convención de ausente≠vacío que rige el resto del cuerpo,
  // aplicada donde tocaba. Y queda MEJOR que hoy: X1 sin marca era last-write-wins sin comprobar
  // nada; N16 con nulo explícito sí comprueba, así que si otro escribió entretanto la marca
  // persistida ya no es nula y llega el 409 de siempre.
  const markerForBody = (expectedMarker === '' || expectedMarker === undefined) ? null : expectedMarker
  // The rows CURRENT when a save resolves, not the ones of the click: a re-read of the previous save
  // can land while this one is in flight, and binding to the click's rows would drop the overlay.
  const currentRows = useRef({ header, sheet })
  currentRows.current = { header, sheet }

  const clearFieldError = (field) => setFieldErrors((prev) => {
    if (!(field in prev)) return prev
    const next = { ...prev }
    delete next[field]
    return next
  })

  const handleDiscard = () => {
    batch.discard()
    setSaveError('')
    setFieldErrors({})
    setConflict(null)
    setDeliveryConfirmation(null)
  }

  // RN27/RN2 (S102): la cantidad no entra en este cuerpo pase lo que pase en la tanda, porque este
  // componente nunca llama a `setField('quantity', ...)`. Modificar cantidad es un flujo aparte
  // (`onOpenModifyQuantity`), y con N16 el campo **ni siquiera existe** en el cuerpo.
  //
  // N16 (addendum 9 / E15): cuerpo **PARCIAL**. "Lo que no viaja, no se toca" — así que aquí solo
  // entra lo que el usuario editó. La invariante del addendum 6 (la fila completa) era de X1 y **no
  // se hereda**: reenviarlo todo "por si acaso" perdería la ventaja y reintroduciría el ruido que
  // hacía lento el guardado.
  //
  // La distinción que hace esto delicado: **ausente ≠ vacío**. Sun lee el JSON crudo
  // (`FoDetailGeneralEditJsonMapper.java:73`, `JsonNode.has()`), así que una clave ausente significa
  // "no tocar" y una clave presente con `null` significa **borrar**. Por eso un campo que el usuario
  // vació viaja explícitamente como `null`, y uno que no tocó no viaja en absoluto.
  const buildSaveBody = () => {
    const body = { expectedLastModified: markerForBody }

    // `'' -> null`: vaciar un campo ES una edición, y N16 solo entiende el borrado como `null`.
    const editedValue = (field) => {
      const value = batch.pending[field]
      return value === '' || value === undefined ? null : value
    }

    // S450 (revoca ADDENDUM 10, punto 3): el PO elige día Y HORA, así que el cuerpo ya NO trunca a
    // medianoche — viaja el INSTANTE elegido, serializado como ISO (`toInstantIso`). El truncado
    // que hacía `localDayOf` aquí era exactamente lo que este cambio retira; verificado contra N16
    // en vivo que acepta y persiste la hora (informe final).
    const editedInstant = (field) => {
      const value = editedValue(field)
      return value === null ? null : toInstantIso(value)
    }

    if (isModified('targetDate')) body.targetDate = editedInstant('targetDate')
    if (isModified('deliveryDate')) body.deliveryDate = editedInstant('deliveryDate')
    // S413: la fecha mínima de inicio, por el mismo camino — instante completo, forma `Z`.
    if (isModified('minStartDate')) body.minStartDate = editedInstant('minStartDate')
    // V-2 / contract: a number (or `null`), not the typed text. `runSave` already rejected anything else.
    if (isModified('priority')) {
      const typed = editedValue('priority')
      body.priority = typed === null ? null : parsePriority(typed)
    }
    // ⚠ Viaja el CÓDIGO, no el objeto ni el id: Sun lo resuelve contra el catálogo
    // (`fabricationOrderPlanTypeCode`, addendum 9). Esto nos quita de encima la búsqueda del id — y
    // con ella la clase de defecto de la pasada 26, cuando escribíamos un campo que no existía.
    if (isModified('planType')) body.fabricationOrderPlanTypeCode = editedValue('planType')
    // ADDENDUM 33 (E39) 4. ⚠️ `forecast` NO pasa por `editedValue`: ése mapea `''` a `null` porque
    // vaciar un texto es borrarlo, y aquí `false` es un valor legítimo, no un hueco. Sun lo lee con
    // `value.asBoolean()` y distingue `null` (borrar) de `false` (es que no), así que colarlo por
    // el camino de los textos convertiría "desmarcar" en "borrar".
    // ⚠️⚠️ EL CLIENTE VIAJA COMO REFERENCIA, `{ id }`, y `null` lo DESVINCULA
    // (`FoDetailGeneralEditJsonMapper.java:190-198`). No pasa por `editedValue` y el motivo es la
    // trampa de esa puerta: Sun lee un objeto SIN `id` no nulo como desvincular, no como error, así
    // que un `{ id: undefined }` colado desde aquí borraría el cliente en silencio y devolvería
    // 200. Por eso el vacío se escribe como `null` EXPLÍCITO —que es lo que el usuario eligió— y el
    // id se manda solo cuando es un número de verdad.
    if (isModified('client')) {
      const chosenId = Number(batch.pending.client)
      body.client = batch.pending.client === '' || !Number.isFinite(chosenId) ? null : { id: chosenId }
    }
    // Same reference contract as the client: `{ id }` links, explicit `null` unlinks.
    if (isModified('parentFo')) body.parentFo = batch.pending.parentFo && batch.pending.parentFo.id ? { id: batch.pending.parentFo.id } : null
    if (isModified('forecast')) body.forecast = !!batch.pending.forecast
    // La fecha sí: mismo camino que `targetDate` (S450) — instante completo, sin truncar.
    if (isModified('expirationDate')) body.expirationDate = editedInstant('expirationDate')

    // Columnas extendidas: solo las editadas, y con el nombre que N16 declara —`extendedColumn1`,
    // camelCase— que NO es el de la fila de X2 (`extended_column1`, el nombre real de la columna).
    // Son dos nombres para el mismo dato en dos puertas distintas; verificado contra
    // `FoDetailGeneralEditJsonMapper.java:42`.
    //
    // ⚠️⚠️ S450, dictamen de Sun: N16 corre en Java 8, y `Instant.parse` SOLO admite la forma con
    // `Z` (offset cero) — `2026-09-23T14:35:20.000Z`, nunca `+02:00`, que da error. Por eso una
    // columna extendida de tipo fecha viaja como ISO explícito (`toInstantIso`, la MISMA conversión
    // de `targetDate`/`deliveryDate`/`expirationDate`) y no como el `Date` crudo que deja el
    // picker — confiar en que `JSON.stringify` lo serialice por su cuenta es frágil: `toISOString`
    // sí produce siempre `Z`, pero nada en esta línea lo garantizaba hasta ahora.
    extendedColumns.forEach((column) => {
      if (!isModified(column.field)) return
      const slot = String(column.field).replace(/^extended_?column/i, '')
      const rawValue = editedValue(column.field)
      if (column.type === 'dateTimePicker' && rawValue !== null) {
        body[`extendedColumn${slot}`] = toInstantIso(rawValue)
      } else {
        // A numeric column goes with a dot decimal, whatever the user typed (`5,5` -> `5.5`).
        body[`extendedColumn${slot}`] = column.numeric && rawValue !== null ? normalizeDecimal(rawValue) : rawValue
      }
    })

    return body
  }

  const runSave = async (confirmedDeliveryDate) => {
    if (saveInFlight.current) return false
    saveInFlight.current = true
    try {
      return await saveBatch(confirmedDeliveryDate)
    } finally {
      saveInFlight.current = false
    }
  }

  const saveBatch = async (confirmedDeliveryDate) => {
    // A half-typed picker hands us an Invalid Date, and `toInstantIso` turns that into `null` —
    // which in this PARTIAL body means DELETE the date. Stop it here, on its own field.
    const invalidDates = Object.keys(batch.pending).filter((field) => {
      const value = batch.pending[field]
      return value instanceof Date && Number.isNaN(value.getTime())
    })
    if (invalidDates.length > 0) {
      setFieldErrors(invalidDates.reduce((errors, field) => ({ ...errors, [field]: t_('Invalid date') }), {}))
      setSaveError('')
      return false
    }
    // The same browser-side checks the legacy list window runs before saving a row.
    const rowErrors = legacyRowErrors({
      pending: batch.pending,
      priority: valueOf('priority'),
      targetDate: valueOf('targetDate'),
      orderDate: source.order?.date || sheetSource.date || '',
      mandatoryFields: extendedColumns.filter((column) => column.mandatory).map((column) => column.field),
      numericFields: extendedColumns.filter((column) => column.numeric).map((column) => column.field),
      valueOf: extendedValueOf
    }, t_)
    if (Object.keys(rowErrors).length > 0) {
      setFieldErrors(rowErrors)
      setSaveError('')
      return false
    }
    setSaving(true)
    setSaveError('')
    try {
      // S94/RN77: la marca que viaja es la que esta ficha leyó, nunca una re-derivada.
      const result = await saveGeneralDetail(fabricationOrderId, buildSaveBody(), confirmedDeliveryDate)
      // La ficha NO se cierra al guardar, así que la marca fresca de la respuesta es la que tiene que
      // usar el guardado siguiente: sin esto, el segundo guardado del usuario sería rechazado por su
      // propio primero (addendum 9).
      const { header: currentHeader, sheet: currentSheet } = currentRows.current
      if (result?.lastModified) batch.setFreshMarker({ value: result.lastModified, forLastModified: String(currentHeader?.lastModified ?? '') })
      const saved = { ...batch.pending }
      setSavedBatch((previous) => ({
        header: currentHeader,
        headerValues: previous.header === currentHeader ? { ...previous.headerValues, ...saved } : saved,
        sheet: currentSheet,
        sheetValues: previous.sheet === currentSheet ? { ...previous.sheetValues, ...saved } : saved
      }))
      batch.discard()
      setFieldErrors({})
      setConflict(null)
      setDeliveryConfirmation(null)
      onSaved(result)
      return true
    } catch (error) {
      if (isFieldValidationError(error)) {
        // E1/RN29: the whole batch stays alive with what the user typed — nothing is cleared,
        // nothing else in the batch is considered saved. Every error must land somewhere visible:
        // on its control when it has one, in the bar when it does not.
        const renderedKeys = ['targetDate', 'deliveryDate', 'minStartDate', 'priority', 'planType', 'expirationDate', ...extendedColumns.map((column) => column.field)]
        const { byField, unplaced } = splitFieldErrors(error.data.errors, renderedKeys)
        setFieldErrors(byField)
        setSaveError(unplaced.join(' · '))
        return false
      }
      if (isConcurrentChangeError(error)) {
        // E2/RN77/D1: GeneralBlock keeps what the user typed (never touches `batch`) and shows the
        // conflict. The 409 carries the CURRENT stamp: adopting it is what lets the next "Save"
        // be the user's informed decision instead of another 409 — keeping the stale one locked
        // every later save, whatever the field, until the dialog was reopened.
        const nextConflict = concurrentChangeConflict(error.data)
        if (nextConflict.lastModified) batch.setFreshMarker({ value: nextConflict.lastModified, forLastModified: String(currentRows.current.header?.lastModified ?? '') })
        setConflict(nextConflict)
        return false
      }
      if (error?.status === 409 && error?.data?.suggestedDate) {
        // E3: same RequiresConfirmationDto contract FabricationOrder.jsx already handles for the
        // row-level flow — accepting resends the SAME batch with confirmedDeliveryDate set.
        setDeliveryConfirmation(error.data)
        return false
      }
      // ⚠ Defecto encontrado al verificar N16 en la app (pasada 29): cualquier error que no fuera
      // validación por campo, 409 de concurrencia o confirmación de entrega se tragaba en silencio.
      // Vivido: el guardado devolvió 404 y la pantalla no dijo NADA — la barra seguía ahí, el botón
      // volvía a habilitarse y el usuario no podía saber si había guardado. Un fallo que no se ve es
      // peor que un fallo.
      setSaveError(error?.message || t_('The changes could not be saved'))
      return false
    } finally {
      setSaving(false)
    }
  }

  const handleSave = () => runSave()
  const handleConfirmDelivery = () => runSave(deliveryConfirmation.suggestedDate)
  const handleCancelDeliveryConfirmation = () => setDeliveryConfirmation(null)

  // The registered function is the same for the whole life of the block and always runs the latest
  // `handleSave`: the container calls it from the unsaved-changes prompt, and a closure frozen at
  // registration would send an old `lastModified` after a 409.
  const saveRef = useRef(handleSave)
  saveRef.current = handleSave
  useEffect(() => {
    registerSave(() => saveRef.current())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { fieldErrors, clearFieldError, saveError, conflict, deliveryConfirmation, saving, handleSave, handleConfirmDelivery, handleCancelDeliveryConfirmation, handleDiscard }
}

export default useGeneralSave
